import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Search } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/shared/components/ui/select'
import { apiClient } from '@/shared/lib/api/api-client'
import type { BankAccount } from '../types/bank-account'
import type { CountryWithInstitutions, WizardHeaderCountry } from '../lib/countries-with-institutions'
import {
  ConnectivityType, canChangeConnectivity, isOpenBankingConnectivity, type ConnectivityValue,
} from '../lib/account-connectivity'
import { WizardOpenBankingPanel } from './wizard-openbanking-panel'
import { WizardConnectivityRadioGroup } from './wizard-connectivity-radio-group'
import { WizardAccountForm } from './wizard-account-form'

interface Company {
  Id: number
  Name: string
}

const NO_COMPANY = '__none__'

interface WizardConfigureStepProps {
  // Null while editing: the country only scopes the create-mode paths.
  country: CountryWithInstitutions | null
  headerCountry: WizardHeaderCountry | null
  connectivity: ConnectivityValue
  onConnectivityChange: (connectivity: ConnectivityValue) => void
  editingAccount: BankAccount | null
  isEditing: boolean
  onBack: () => void
  onDone: () => void
  onBusyChange: (busy: boolean) => void
}

/**
 * Second wizard step: a search + company toolbar for the Open Banking route
 * (reusing the original bank-selection layout) and a single-select connectivity
 * radio group, followed by either the Open Banking bank list or the shared
 * account form. Every non-Open-Banking connectivity renders the form directly,
 * picking its institution from a global dropdown inside the form itself. In
 * edit mode the form is always shown directly, and the connectivity selector
 * only unlocks when the account may change its connection (UC-094).
 */
export function WizardConfigureStep({
  country, headerCountry, connectivity, onConnectivityChange, editingAccount, isEditing,
  onBack, onDone, onBusyChange,
}: WizardConfigureStepProps) {
  const [search, setSearch] = useState('')
  const [companyId, setCompanyId] = useState(0)

  const showOpenBankingPanel = !isEditing && country !== null && isOpenBankingConnectivity(connectivity)

  const { data: companies = [] } = useQuery<Company[]>({
    queryKey: ['companies'],
    queryFn: () => apiClient.get<Company[]>('/api/companies'),
    staleTime: 10 * 60 * 1000,
    enabled: showOpenBankingPanel,
  })

  function handleConnectivityChange(next: ConnectivityValue) {
    // Only Open Banking has a search + company toolbar and its own bank list;
    // every other connectivity renders the form directly, so switching away
    // from Open Banking never needs to preserve any picker state here.
    setSearch('')
    onConnectivityChange(next)
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        {headerCountry && (
          <>
            <span className={`fi fi-${headerCountry.CountryCode.toLowerCase()}`} />
            <span className="font-medium">{headerCountry.CountryName}</span>
          </>
        )}
      </div>

      {showOpenBankingPanel && (
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search bank…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="pl-8 h-9"
            />
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-muted-foreground whitespace-nowrap">Company</span>
            <Select
              value={companyId ? String(companyId) : NO_COMPANY}
              onValueChange={(value) => setCompanyId(value === NO_COMPANY ? 0 : Number(value))}
            >
              <SelectTrigger className="w-48 h-9">
                <SelectValue placeholder="Select company…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_COMPANY}>Select company…</SelectItem>
                {companies.map((company) => (
                  <SelectItem key={company.Id} value={String(company.Id)}>{company.Name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <WizardConnectivityRadioGroup
        connectivity={connectivity}
        isEditing={isEditing}
        canChangeConnectivity={editingAccount !== null && canChangeConnectivity(editingAccount)}
        onChange={handleConnectivityChange}
      />

      {showOpenBankingPanel ? (
        <>
          <WizardOpenBankingPanel
            country={country}
            companyId={companyId}
            search={search}
            onBusyChange={onBusyChange}
          />
          <button
            type="button"
            className="self-start text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
            onClick={() => handleConnectivityChange(ConnectivityType.ManualUpload)}
          >
            I can&apos;t find my bank
          </button>
        </>
      ) : (
        <WizardAccountForm
          connectivity={connectivity}
          countryCode={country?.CountryCode}
          editingAccount={editingAccount}
          onDone={onDone}
          onBusyChange={onBusyChange}
        />
      )}
    </div>
  )
}
